// The one Position Editor. Every entry point opens it: Portfolio «＋ Добавить
// позицию», «Только наблюдение → Указать покупку», Settings «Монета добавлена →
// Указать покупку», the Signals hint, Orca AI «Указать позицию», and «＋ Добавить
// покупку» in a position. A position is a list of purchases (lots): the first
// purchase opens it, every next one is a докупка — quantities add up and the
// average price is re-weighted by the server, never typed in by the user.
import { MOCK_DATA } from '@/lib/api/mode';
import { PAGES } from '@/app/nav';
import i18n from '@/i18n';
import { useT } from '@/i18n';
import { useCaps } from '@/hooks/useCaps';
import { useUpgrade } from '@/features/plans/Upgrade';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useToast } from '@/app/providers/Toast';
import { XButton } from '@/components/ui/Buttons';
import { CoinIcon } from '@/components/ui/CoinIcon';
import { Icon } from '@/components/ui/Icon';
import { useSheet } from '@/components/ui/Sheet';
import { TextField } from '@/features/profile/Fields';
import { usePortfolio } from '@/hooks/derived';
import { useAccount, useAccountAction, useMarket, usePortfolioAction } from '@/hooks/queries';
import { accountApi, api, historyApi, portfolioApi } from '@/lib/api/client';
import { cx, dir, nf, pct, qty, susd, usd } from '@/lib/format';
import { aggregate, withLot } from '@/lib/position';
import { lotDateLabel, monthLabel, sinceLabel } from '@/lib/time';
import type { Coin, HistoryCheck, Holding, Portfolio, PriceEstimate, PurchaseLot } from '@/types';
import { PositionDetail as LazyDetail } from './PositionSheets';
import { positionNudge, positionWhy } from './copy';
import { PurchaseDate, pdateOf, type PDate } from './PurchaseDate';

export type LotInput = Omit<PurchaseLot, 'id'>;
/** a number typed in any of the UI languages: «0,5» · «0.5» · «1 234,5» · «1,234.5» */
const num = (v: string) => {
  const s = v.replace(/[\s\u00a0\u202f]/g, '');
  const dec = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
  return Number(dec < 0 ? s : `${s.slice(0, dec).replace(/[.,]/g, '')}.${s.slice(dec + 1)}`);
};
const lotId = () => `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/* ---------- portfolio writes (optimistic, same aggregate as the server) ---------- */
const put = (p: Portfolio, symbol: string, lots: PurchaseLot[]): Portfolio => {
  const rest = p.holdings.filter((h) => h.symbol !== symbol);
  return { ...p, holdings: lots.length ? (p.holdings.some((h) => h.symbol === symbol) ? p.holdings.map((h) => (h.symbol === symbol ? aggregate(symbol, lots) : h)) : [...rest, aggregate(symbol, lots)]) : rest };
};
const lotsOf = (p: Portfolio, s: string) => p.holdings.find((h) => h.symbol === s)?.lots ?? [];

export function usePositionActions() {
  const toast = useToast();
  const addLot = usePortfolioAction(portfolioApi.addLot, (cur, s: string, lot: PurchaseLot) => put(cur, s, [...lotsOf(cur, s), lot]));
  const updateLot = usePortfolioAction(portfolioApi.updateLot, (cur, s: string, id: string, lot: LotInput) => put(cur, s, lotsOf(cur, s).map((l) => (l.id === id ? { ...lot, id } : l))));
  const removeLot = usePortfolioAction(portfolioApi.removeLot, (cur, s: string, id: string) => put(cur, s, lotsOf(cur, s).filter((l) => l.id !== id)));
  const removePosition = usePortfolioAction(portfolioApi.removePosition, (cur, s: string) => put(cur, s, []));
  // the confirmation shows once the server saved it; a refusal shows the server's reason
  const done = (p: Promise<unknown>, ok?: string) => p.then(() => { if (ok) toast(ok); }, (e: Error) => toast(e.message));
  return {
    addLot: (s: string, lot: LotInput, ok?: string) => done(addLot.run(s, { ...lot, id: lotId() }), ok),
    updateLot: (s: string, id: string, lot: LotInput, ok?: string) => done(updateLot.run(s, id, lot), ok),
    removeLot: (s: string, id: string, ok?: string) => done(removeLot.run(s, id), ok),
    removePosition: (s: string, ok?: string) => done(removePosition.run(s), ok),
  };
}

/** «Мои монеты» (account) — a position exists only for a tracked coin, as in the bot. */
function useTrack() {
  const toast = useToast();
  const account = useAccount();
  const action = useAccountAction(accountApi.setCoins, (cur, next: string[]) => ({ ...cur, coins: next }));
  return {
    coins: account.coins, limit: account.plan.coinLimit, planName: account.plan.name,
    add: (c: string) => action.run([...account.coins, c]).catch((e: Error) => toast(e.message)),
  };
}

/* ---------- one purchase: quantity, price, date ---------- */
// Two different situations, never mixed:
//  A. The user knows the price, the date only roughly — as in the bot: Orca checks
//     the price against that month's daily ranges (±3%) and offers the day
//     (one day → «≈ дата»; several → pick or «Не знаю точно»; none → change price /
//     period / save without a day; history unavailable → the month is kept).
//  B. NEW (web first, not in the bot yet): the user doesn't remember the price —
//     Orca estimates it from market history: a day → that day's close; a month →
//     the median daily close, with the month's range. The user confirms it
//     («Использовать ≈ $…») or types their own. Estimated prices always show «≈».
//  Neither price nor date → the purchase can be saved with the quantity only;
//  invested and P/L are not calculated (nothing is invented).
type Check = { key: string } & ({ status: 'searching' } | HistoryCheck);
type Est = { key: string } & ({ status: 'loading' } | PriceEstimate);
type PriceMode = 'user' | 'estimate' | 'none';
const fmtPrice = (v: number, c: Coin) => usd(v, v >= 100 ? 0 : c.decimals);
const isEstimated = (l?: PurchaseLot) => !!l?.priceSource && l.priceSource !== 'user' && l.price !== undefined;

export function LotForm({ coin, held, initial, submitLabel, onSubmit, onCancel, autoFocus = true }: {
  coin: Coin; held?: Holding; initial?: PurchaseLot; submitLabel: string;
  onSubmit: (lot: LotInput) => void; onCancel?: () => void; autoFocus?: boolean;
}) {
  const { t } = useT('portfolio');
  const [q, setQ] = useState(initial ? String(initial.qty) : '');
  // editing a purchase without a price opens the price field: that is what the user came to fill in
  const [priceMode, setPriceMode] = useState<PriceMode>(() => (isEstimated(initial) ? 'estimate' : 'user'));
  // no silent default: the price is what the user paid (the current price is one tap away)
  const [price, setPrice] = useState(initial && !isEstimated(initial) && initial.price !== undefined ? String(initial.price) : '');
  const [date, setDate] = useState<PDate>(() => pdateOf(initial));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const priceRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);
  const qn = num(q), pn = num(price);
  const userPrice = priceMode === 'user' && pn > 0;
  const clearErr = (k: string) => setErrors((x) => ({ ...x, [k]: '' }));

  const day = date.mode === 'exact' ? date.since : undefined;
  const period = date.mode === 'approx' ? date.since : undefined;

  /* ---- A. known price + approximate month → which day (the bot's check) ---- */
  const key = priceMode === 'user' && period && pn > 0 ? `${coin.symbol}|${pn}|${period}` : '';
  const keptKey = !isEstimated(initial) && initial?.period && initial.dateSource === 'history' ? `${coin.symbol}|${initial.price}|${initial.period}` : '';
  const [check, setCheck] = useState<Check | null>(() => (keptKey ? { key: keptKey, status: 'single_match', candidates: [{ day: initial!.date!, low: 0, high: 0 }] } : null));
  /** the day chosen: a string — that day; null — «не знаю точно» / save without a day; undefined — not decided */
  const [pick, setPick] = useState<string | null | undefined>(initial?.dateSource === 'history' ? initial.date : undefined);
  const [runs, setRuns] = useState(0);
  useEffect(() => {
    if (!key) { setCheck(null); return; }
    if (key === keptKey && runs === 0) return; // an unchanged purchase keeps its confirmed day
    setCheck({ key, status: 'searching' }); setPick(undefined);
    let live = true;
    const t = window.setTimeout(() => {
      historyApi.checkPeriod(coin.symbol, pn, period!)
        .then((r) => { if (!live) return; setCheck({ key, ...r }); if (r.status === 'single_match') setPick(r.candidates[0].day); })
        .catch(() => { if (live) setCheck({ key, status: 'unavailable', candidates: [] }); });
    }, 500);
    return () => { live = false; window.clearTimeout(t); };
  }, [key, runs]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- B. unknown price + a day or a month → Orca's estimate (new) ---- */
  const ekey = priceMode === 'estimate' && (day || period) ? `${coin.symbol}|${day ? `d:${day}` : `m:${period}`}` : '';
  const ekeptKey = isEstimated(initial) ? `${coin.symbol}|${initial!.priceSource === 'history_close' ? `d:${initial!.date}` : `m:${initial!.period}`}` : '';
  const [est, setEst] = useState<Est | null>(null);
  /** the confirmed estimate (kept for an unchanged purchase; a new day / month needs a new confirmation) */
  const [accepted, setAccepted] = useState<{ key: string; price: number; source: 'history_close' | 'history_median' } | null>(() =>
    isEstimated(initial) ? { key: ekeptKey, price: initial!.price!, source: initial!.priceSource as 'history_close' | 'history_median' } : null);
  const [eruns, setEruns] = useState(0);
  useEffect(() => {
    if (!ekey) { setEst(null); return; }
    setEst({ key: ekey, status: 'loading' });
    let live = true;
    const t = window.setTimeout(() => {
      historyApi.estimatePrice(coin.symbol, day ? { day } : { period: period! })
        .then((r) => { if (live) setEst({ key: ekey, ...r }); })
        .catch(() => { if (live) setEst({ key: ekey, status: 'unavailable' }); });
    }, 350);
    return () => { live = false; window.clearTimeout(t); };
  }, [ekey, eruns]); // eslint-disable-line react-hooks/exhaustive-deps
  const acceptedNow = accepted && accepted.key === ekey ? accepted : null;
  const estPrice = priceMode === 'estimate' ? acceptedNow?.price : undefined;

  const lotDate = (): Partial<LotInput> => {
    if (date.mode === 'exact' && date.since) return { date: date.since, dateSource: 'user' };
    if (date.mode === 'approx' && date.since) return { period: date.since, ...(priceMode === 'user' && pick ? { date: pick, dateSource: 'history' as const } : {}) };
    return {};
  };
  const lotPrice = (): Partial<LotInput> => (priceMode === 'user' ? { price: pn, priceSource: 'user' }
    : priceMode === 'estimate' && acceptedNow ? { price: acceptedNow.price, priceSource: acceptedNow.source } : {});
  const unitPrice = userPrice ? pn : estPrice;
  const valid = qn > 0 && !!unitPrice;
  const approx = priceMode === 'estimate';
  // a new purchase on top of a position: show what the position becomes
  const next = qn > 0 && held && !initial ? withLot(held, coin.symbol, { id: 'preview', qty: qn, ...lotPrice(), ...lotDate() }) : null;
  const value = valid ? qn * coin.price : 0;
  const pnl = valid ? value - qn * unitPrice! : 0;
  const searching = (!!key && check?.status === 'searching') || (!!ekey && est?.status === 'loading');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const err: Record<string, string> = {};
    if (!(qn > 0)) err.q = t('err.qty');
    if (priceMode === 'user' && !(pn > 0)) err.price = t('err.price');
    if (priceMode === 'estimate' && !acceptedNow) err.price = ekey ? t('err.confirmEstimate') : t('err.needDate');
    if (userPrice && period && check?.status === 'multiple_matches' && pick === undefined) err.date = t('err.pickDay');
    if (userPrice && period && check?.status === 'no_match' && pick === undefined) err.date = t('err.noMatch');
    setErrors(err);
    if (Object.keys(err).length || searching) return;
    onSubmit({ qty: qn, ...lotPrice(), ...lotDate() });
  };
  const month = period ? monthLabel(period) : '';
  const whenLabel = day ? lotDateLabel({ date: day }) : period ? monthLabel(period) : '';
  const toUser = () => { setPriceMode('user'); setPrice(''); clearErr('price'); window.setTimeout(() => priceRef.current?.querySelectorAll('input')[1]?.focus(), 30); };

  return (
    <form className="pfs lotf" onSubmit={submit} noValidate>
      <div className="pfs-grid" ref={priceRef}>
        <TextField label={held && !initial ? t('form.howMany') : t('form.qty')} inputMode="decimal" placeholder={nf(0.5, 1)} value={q} error={errors.q} autoFocus={autoFocus}
          onChange={(e) => { setQ(e.target.value); clearErr('q'); }} />
        {priceMode === 'user' ? (
          <TextField label={t('form.price')} inputMode="decimal" placeholder={t('form.pricePh', { v: nf(coin.price, coin.decimals) })} value={price} error={errors.price} keepHint
            hint={<span className="pfs-hints">
              <button type="button" className="pfs-chip" onClick={() => { setPrice(String(coin.price)); clearErr('price'); }}>{t('form.current', { v: usd(coin.price, coin.decimals) })}</button>
              <button type="button" className="pfs-chip" onClick={() => { setPriceMode('estimate'); clearErr('price'); }}>{t('form.dontRemember')}</button>
              <button type="button" className="pfs-chip" onClick={() => { setPriceMode('none'); clearErr('price'); }}>{t('form.noPrice')}</button>
            </span>}
            onChange={(e) => { setPrice(e.target.value); clearErr('price'); }} />
        ) : (
          <div className={cx('pf-field est-field', errors.price && 'bad')}>
            <label>{t('form.price')}</label>
            <div className={cx('est-box', priceMode === 'none' && 'none')}>
              {priceMode === 'none' ? <b>{t('form.notSet')}</b> : acceptedNow ? <b>≈ {fmtPrice(acceptedNow.price, coin)}</b> : <span>{t('form.orcaEstimates')}</span>}
              <button type="button" className="pfs-chip" onClick={toUser}>{priceMode === 'none' ? t('form.setPrice') : t('form.ownPrice')}</button>
            </div>
            {errors.price ? <small className="pf-err">{errors.price}</small> : <small className="pf-hint">{priceMode === 'none' ? t('form.noPriceHint') : t('form.estimateHint')}</small>}
          </div>
        )}
      </div>
      {priceMode === 'user' && !(pn > 0) && (
        <p className="est-nudge">{positionNudge()} {t('form.nudgeNoPrice')}</p>
      )}
      <div ref={dateRef}><PurchaseDate value={date} estimating={priceMode === 'estimate'} onChange={(d) => { setDate(d); clearErr('date'); clearErr('price'); }} /></div>

      {priceMode === 'estimate' && (
        <div className={cx('hist est', est && est.status !== 'loading' && est.status, acceptedNow && 'accepted')} aria-live="polite">
          {!ekey ? (
            date.mode === 'none' ? (
              <>
                <p className="hist-t"><Icon name="alert" /><span><b>{t('est.noDateNoPrice')}</b>{t('est.noDateNoPriceText', { coin: coin.symbol })}</span></p>
                <div className="hist-acts">
                  <button type="button" onClick={() => setDate({ mode: 'approx' })}>{t('est.setPeriod')}</button>
                  <button type="button" onClick={() => { setPriceMode('none'); clearErr('price'); }}>{t('est.keepNoPrice')}</button>
                </div>
              </>
            ) : <p className="hist-t"><Icon name="cal" /><span>{date.mode === 'exact' ? t('est.pickDay', { coin: coin.symbol }) : t('est.pickMonth', { coin: coin.symbol })}</span></p>
          ) : !est || est.status === 'loading' ? (
            <p className="hist-t"><span className="hist-spin" aria-hidden="true" /><span>{day ? t('est.estimatingDay', { coin: coin.symbol, date: whenLabel }) : t('est.estimatingMonth', { coin: coin.symbol, month })}</span></p>
          ) : est.status === 'ok' ? (
            <>
              <div className="est-res">
                <b className="est-p">≈ {fmtPrice(est.price!, coin)}</b>
                <span>{est.method === 'close' ? t('est.close', { coin: coin.symbol, date: whenLabel }) : t('est.median', { coin: coin.symbol, month: monthLabel(period!) })}</span>
                {est.method === 'median_close' && <small>{t('est.range', { coin: coin.symbol, month, low: fmtPrice(est.low!, coin), high: fmtPrice(est.high!, coin) })}</small>}
              </div>
              <div className="hist-acts">
                <button type="button" className={cx(acceptedNow && 'on')} aria-pressed={!!acceptedNow}
                  onClick={() => { setAccepted({ key: ekey, price: est.price!, source: est.method === 'close' ? 'history_close' : 'history_median' }); clearErr('price'); }}>
                  {acceptedNow ? `✓ ${t('est.using', { v: `≈ ${fmtPrice(est.price!, coin)}` })}` : t('est.use', { v: `≈ ${fmtPrice(est.price!, coin)}` })}
                </button>
                <button type="button" onClick={toUser}>{t('est.enterOwn')}</button>
                <button type="button" onClick={() => { setPriceMode('none'); clearErr('price'); }}>{t('est.noPrice')}</button>
              </div>
            </>
          ) : est.status === 'not_traded' ? (
            <>
              <p className="hist-t"><Icon name="alert" /><span><b>{t('est.notTraded', { coin: coin.symbol })}</b>{t('est.notTradedText', { coin: coin.symbol, date: lotDateLabel({ date: est.listed }) })}</span></p>
              <div className="hist-acts"><button type="button" onClick={() => dateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>{t('est.changeDate')}</button><button type="button" onClick={toUser}>{t('est.enterOwn')}</button><button type="button" onClick={() => { setPriceMode('none'); clearErr('price'); }}>{t('est.noPrice')}</button></div>
            </>
          ) : (
            <>
              <p className="hist-t"><Icon name="alert" /><span><b>{t('est.down')}</b>{t('est.downText')}</span></p>
              <div className="hist-acts">
                <button type="button" onClick={() => setEruns((n) => n + 1)}>{t('common:action.retry')}</button>
                <button type="button" onClick={toUser}>{t('est.enterOwn')}</button>
                <button type="button" onClick={() => { setPriceMode('none'); clearErr('price'); }}>{t('est.keepNoPrice')}</button>
              </div>
            </>
          )}
        </div>
      )}
      {priceMode === 'none' && (
        <p className="hist hist-t quiet"><Icon name="alert" /><span>{t('est.quietNoPrice', { coin: coin.symbol })} <button type="button" className="hist-link inl" onClick={() => setPriceMode('estimate')}>{t('est.estimateByDate')}</button>.</span></p>
      )}

      {userPrice && period && (
        <div className={cx('hist', check && check.status !== 'searching' && check.status.replace('_', '-'))} aria-live="polite">
          {!check || check.status === 'searching' ? (
            <p className="hist-t"><span className="hist-spin" aria-hidden="true" /><span>{t('check.checking', { price: fmtPrice(pn, coin), coin: coin.symbol, month })}</span></p>
          ) : check.status === 'single_match' ? (
            <p className="hist-t"><Icon name="check" /><span><b>≈ {lotDateLabel({ date: pick ?? check.candidates[0].day }).replace('≈ ', '')}</b>{t('check.single', { coin: coin.symbol })}{pick === null ? ` ${t('check.noDay')}` : ''}</span>
              {pick !== null ? <button type="button" className="hist-link" onClick={() => setPick(null)}>{t('check.dontKnow')}</button> : <button type="button" className="hist-link" onClick={() => setPick(check.candidates[0].day)}>{t('check.takeDay')}</button>}</p>
          ) : check.status === 'multiple_matches' ? (
            <>
              <p className="hist-t"><Icon name="cal" /><span><b>{t('check.multiple')}</b>{t('check.multipleText', { month, price: fmtPrice(pn, coin) })}</span></p>
              <div className="hist-days">
                {check.candidates.map((c) => <button key={c.day} type="button" className={cx(pick === c.day && 'on')} aria-pressed={pick === c.day} onClick={() => { setPick(c.day); clearErr('date'); }}>{lotDateLabel({ date: c.day })}</button>)}
                <button type="button" className={cx('skip', pick === null && 'on')} aria-pressed={pick === null} onClick={() => { setPick(null); clearErr('date'); }}>{t('check.dontKnow')}</button>
              </div>
            </>
          ) : check.status === 'no_match' ? (
            <>
              <p className="hist-t"><Icon name="alert" /><span><b>{t('check.noMatch')}</b>{t('check.noMatchText', { coin: coin.symbol, month, price: fmtPrice(pn, coin) })}</span></p>
              <div className="hist-acts">
                <button type="button" onClick={() => priceRef.current?.querySelectorAll('input')[1]?.focus()}>{t('check.changePrice')}</button>
                <button type="button" onClick={() => dateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>{t('check.changePeriod')}</button>
                <button type="button" className={cx(pick === null && 'on')} onClick={() => { setPick(null); clearErr('date'); }}>{pick === null ? `✓ ${t('check.withoutDate')}` : t('check.saveWithoutDate')}</button>
              </div>
            </>
          ) : (
            <p className="hist-t"><Icon name="alert" /><span><b>{t('est.down')}</b>{t('check.downText', { month: monthLabel(period) })}</span>
              <button type="button" className="hist-link" onClick={() => setRuns((n) => n + 1)}>{t('common:action.retry')}</button></p>
          )}
          {errors.date && <p className="pf-err">{errors.date}</p>}
        </div>
      )}

      {next ? (
        <div className={cx('pfs-sum', 'after')}>
          <div><span>{t('sum.newQty')}</span><b>{qty(next.qty)} {coin.symbol}</b></div>
          <div><span>{t('sum.newAvg')}</span><b>{next.pricedQty ? `${next.estimated ? '≈ ' : ''}${usd(next.avgPrice, coin.decimals)}` : '—'}</b><small>{t('sum.was', { v: held!.pricedQty ? usd(held!.avgPrice, coin.decimals) : '—' })}</small></div>
        </div>
      ) : !initial && (
        <div className={cx('pfs-sum', !valid && 'off')}>
          <div><span>{t('sum.valueNow')}</span><b>{qn > 0 ? usd(qn * coin.price, 2) : '—'}</b></div>
          {/* ORCA API: P/L is calculated by the server once the purchase is saved */}
          {MOCK_DATA && <div><span>{t('table.pnl')}</span><b className={dir(pnl)}>{valid ? `${approx ? '≈ ' : ''}${susd(pnl, 2)} · ${approx ? '≈ ' : ''}${pct((pnl / (qn * unitPrice!)) * 100, 1)}` : '—'}</b></div>}
        </div>
      )}
      <div className="pfs-acts">
        <XButton inv label={searching ? t('check.searching') : submitLabel} type="submit" sm={!!onCancel} busy={searching} />
        {onCancel ? <button type="button" className="stc-link" onClick={onCancel}>{t('common:action.cancel')}</button> : <span className="pfs-hint">{t('form.submitHint')}</span>}
      </div>
    </form>
  );
}

/* ---------- the editor: pick a coin → track it → a purchase ---------- */
function PositionEditor({ initial }: { initial?: string }) {
  const { universe } = useMarket();
  const track = useTrack();
  const pf = usePortfolio();
  const { addLot } = usePositionActions();
  const { openSheet, closeSheet } = useSheet();
  const toast = useToast();
  const caps = useCaps();
  const upgrade = useUpgrade();
  const { t } = useT('portfolio');
  const [symbol, setSymbol] = useState(initial ?? '');
  const [confirmed, setConfirmed] = useState<string[]>([]);
  const [find, setFind] = useState('');
  const coin = universe.find((c) => c.symbol === symbol);
  const held = pf.rows.find((r) => r.symbol === symbol);
  const list = useMemo(() => universe.filter((c) => !find || c.symbol.toLowerCase().includes(find.toLowerCase()) || c.name.toLowerCase().includes(find.toLowerCase()))
    .sort((a, b) => Number(track.coins.includes(b.symbol)) - Number(track.coins.includes(a.symbol))), [universe, find, track.coins]);
  const tracked = !!coin && (track.coins.includes(coin.symbol) || confirmed.includes(coin.symbol));

  if (!coin) {
    return (
      <div className="pfs">
        <p className="pfs-lead">{t('editor.whichCoin')}</p>
        <label className="cs-search pfs-find"><Icon name="search" /><input type="text" placeholder="BTC, Ethereum…" value={find} onChange={(e) => setFind(e.target.value)} autoFocus /></label>
        <div className="pfs-coins">
          {list.map((c) => {
            const h = pf.rows.some((r) => r.symbol === c.symbol);
            const tr = track.coins.includes(c.symbol);
            return (
              <button key={c.symbol} type="button" className={cx('pfs-coin', h && 'held', !t && 'untracked')} onClick={() => setSymbol(c.symbol)}>
                <CoinIcon symbol={c.symbol} /><b>{c.symbol}</b><small>{h ? t('settings:coins.held') : tr ? t('settings:coins.watch') : t('settings:coins.notTracked')}</small>
              </button>
            );
          })}
          {!list.length && <p className="empty">{t('editor.notFound', { q: find, count: universe.length })}</p>}
        </div>
      </div>
    );
  }
  const picked = (
    <button type="button" className="pfs-picked" onClick={() => setSymbol('')}>
      <CoinIcon symbol={coin.symbol} /><span><b>{coin.symbol}</b><small>{coin.name} · {t('editor.now', { v: usd(coin.price, coin.decimals) })}</small></span><em>{t('common:action.change')}</em>
    </button>
  );
  // not tracked yet: add it to «Мои монеты» first (within the plan's limit)
  if (!tracked) {
    const full = track.coins.length >= track.limit;
    return (
      <div className="pfs">
        {picked}
        <div className={cx('pfs-track', full && 'full')}>
          <b>{full ? t('editor.limit') : t('editor.notTracked')}</b>
          {full ? (
            <>
              <p>{t('editor.limitText', { plan: track.planName, count: track.limit, n: track.coins.length, list: track.coins.join(', '), coin: coin.symbol })}</p>
              <div className="pfs-acts">
                {/* more coins come with a higher plan — offered right where the limit stops the user */}
                {caps.plan !== 'premium' && <XButton inv sm label={t('editor.more', { count: caps.plan === 'free' ? caps.coinLimits.plus : caps.coinLimits.premium, plan: caps.plan === 'free' ? 'Plus' : 'Premium' })} onClick={() => upgrade('more_coins')} />}
                <Link className="sh-open on" to="/settings#coins" onClick={closeSheet}>{t('editor.coinsInSettings')}<span aria-hidden="true">→</span></Link><button type="button" className="stc-link" onClick={() => setSymbol('')}>{t('editor.pickOther')}</button>
              </div>
            </>
          ) : (
            <>
              <p>{t('editor.trackQ', { coin: coin.symbol, n: track.coins.length + 1, max: track.limit })}</p>
              <div className="pfs-acts">
                <XButton inv label={t('editor.track', { coin: coin.symbol })} onClick={() => { track.add(coin.symbol); setConfirmed((x) => [...x, coin.symbol]); toast(t('settings:coins.added', { coin: coin.symbol })); }} />
                <button type="button" className="stc-link" onClick={() => setSymbol('')}>{t('editor.dontTrack')}</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
  const save = (lot: LotInput) => {
    void addLot(coin.symbol, lot, held ? t('toast.lotAdded', { coin: coin.symbol }) : t('toast.posAdded', { coin: coin.symbol }));
    // show the result: the position with its purchases
    openSheet({ kicker: t('pos.kicker'), title: coin.symbol, body: <LazyDetail symbol={coin.symbol} />, className: 'pf-sheet' });
  };
  return (
    <div className="pfs">
      {picked}
      {held && <p className="pfs-note"><Icon name="pie" />{t('editor.youHave', { qty: qty(held.qty), coin: coin.symbol })}{held.pricedQty ? ` ${t('editor.atAvg', { v: `${held.estimated ? '≈ ' : ''}${usd(held.avgPrice, coin.decimals)}` })}` : ''}{held.since ? `, ${t('pos.inPosition', { since: sinceLabel(held.since, held.sinceSource) })}` : ''}. {t('editor.another')}</p>}
      {!held && (
        // first purchase of this coin: why it is worth it (shown once, here)
        <div className="pos-why"><Icon name="pie" /><p><b>{t('editor.why')}</b>{positionWhy()}</p></div>
      )}
      <LotForm key={coin.symbol} coin={coin} held={held} submitLabel={held ? t('addPurchase') : t('addPosition')} onSubmit={save} />
    </div>
  );
}


/** Opens the editor: a new position (optionally for a coin), or one more purchase of a held coin. */
export function usePositionEditor() {
  const { openSheet } = useSheet();
  const pf = usePortfolioSafe();
  return (symbol?: string) => {
    const held = !!symbol && !!pf?.some((h) => h.symbol === symbol);
    openSheet({ kicker: held ? i18n.t('portfolio:editor.kickerLot') : PAGES.portfolio.title, title: held ? i18n.t('portfolio:editor.addLotTitle', { coin: symbol }) : symbol ? i18n.t('portfolio:editor.posTitle', { coin: symbol }) : i18n.t('portfolio:addPosition'), body: <PositionEditor initial={symbol} />, className: 'pf-sheet' });
  };
}
/** the holdings if loaded — the editor can be opened from any page */
/** the account's holdings, or null while they are still loading (never «no positions» by mistake) */
export function usePortfolioSafe() {
  return useQuery({ queryKey: ['portfolio'], queryFn: api.portfolio }).data?.holdings ?? null;
}
